import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Check, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useBreadcrumbs } from '@/components/Breadcrumbs/useBreadcrumbs';
import { InvoiceRunStatus, InvoiceRunType } from '@/graphql/generatedTypes';
import { Routes } from '@/utils/routes';
import { cn } from '@/lib/utils';

import {
  useInvoiceRunDetailQuery,
  type InvoiceRunDetailQuery,
} from './InvoiceRunDetail.generated';
import { useRetryInvoiceRunMutation } from './RetryInvoiceRun.generated';
import { ChargeModal, type ChargeModalRun } from './ChargeModal';
import { ErrorsCard } from './ErrorsCard';
import { CollectionInvoicesCard } from './CollectionInvoicesCard';
import { RightRail } from './RightRail';
import { useResultFileDownload } from './useResultFileDownload';
import { CapabilityChip, GatedButton } from './capability';
import { useInvoicingCapability } from './useInvoicingCapability';
import {
  ChargedPill,
  DryRunPill,
  RunStatusLabel,
  RunTypePill,
} from './RunPills';
import {
  formatCount,
  formatDateTimeShort,
  formatDurationMs,
  formatTimeOnly,
  isCharged,
  isEsStylePeriod,
  isGenerationRun,
  heartbeatSilenceMs,
  isReadyToCharge,
  isRegenerationOpen,
  isRunActive,
  periodLabel,
  periodRangeLabel,
  runPresentationState,
  shortRunId,
} from './invoiceRunPresentation';

type RunDetail = NonNullable<InvoiceRunDetailQuery['invoiceRun']>;

function StalledBanner({ run }: { run: RunDetail }) {
  const silence = heartbeatSilenceMs(run);
  return (
    <div className="tw:flex tw:items-start tw:gap-3 tw:rounded-lg tw:border tw:border-amber-500/50 tw:bg-amber-500/10 tw:p-4">
      <AlertTriangle className="tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-amber-600" />
      <div className="tw:flex tw:flex-col tw:gap-1">
        <p className="tw:text-sm tw:font-semibold">
          No heartbeat for{' '}
          {silence != null ? formatDurationMs(silence) : 'a while'}
        </p>
        <p className="tw:text-sm tw:text-muted-foreground">
          The worker may have died. #fin_automation was alerted. Already-created
          invoices are kept — a retry resumes where this run stopped.
        </p>
      </div>
    </div>
  );
}

function FailedBanner({ run }: { run: RunDetail }) {
  const duration =
    run.started_at && run.finished_at
      ? Date.parse(run.finished_at) - Date.parse(run.started_at)
      : null;
  return (
    <div className="tw:flex tw:items-start tw:gap-3 tw:rounded-lg tw:border tw:border-destructive/50 tw:bg-destructive/5 tw:p-4">
      <AlertTriangle className="tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-destructive" />
      <div className="tw:flex tw:flex-col tw:gap-1">
        <p className="tw:text-sm tw:font-semibold tw:text-destructive">
          Run failed
          {duration != null ? ` after ${formatDurationMs(duration)}` : ''}
        </p>
        {run.error && (
          <p className="tw:text-sm tw:whitespace-pre-wrap tw:text-foreground">
            {run.error}
          </p>
        )}
      </div>
    </div>
  );
}

function InProgressCard({ run }: { run: RunDetail }) {
  const stalled = runPresentationState(run) === 'stalled';
  const processed = run.processed_count ?? 0;
  const total = run.total_count ?? 0;
  const pct = total > 0 ? Math.min(100, (processed / total) * 100) : 0;
  const lines = [
    run.invoice_count != null &&
      `${formatCount(run.invoice_count)} invoices created so far`,
    run.last_activity_at &&
      `Last activity ${formatTimeOnly(run.last_activity_at)}`,
    run.started_at && `Started ${formatDateTimeShort(run.started_at)}`,
    run.error_count != null &&
      run.error_count > 0 &&
      `${formatCount(run.error_count)} errors so far`,
  ].filter(Boolean);
  return (
    <div className="tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-4">
      <div className="tw:flex tw:items-end tw:justify-between tw:gap-3">
        <p className="tw:text-sm tw:font-medium">
          {formatCount(processed)} of {formatCount(total)} stores processed
        </p>
        <span className="tw:text-3xl tw:font-semibold tw:tabular-nums">
          {Math.round(pct)}%
        </span>
      </div>
      <div className="tw:h-2 tw:w-full tw:overflow-hidden tw:rounded-full tw:bg-muted">
        <div
          className={cn(
            'tw:h-full tw:rounded-full',
            stalled ? 'tw:bg-amber-500' : 'tw:bg-sky-600',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="tw:text-sm tw:text-muted-foreground">{lines.join(' · ')}</p>
    </div>
  );
}

function StatTiles({ run }: { run: RunDetail }) {
  // invoice_count counts the entities that ended up with an invoice;
  // processed_count counts every one the run got a result for, most of which
  // are billed nothing. Runs from before invoice_count have it null.
  const invoices = run.invoice_count;
  const checked = run.processed_count;
  const nothingToBill =
    checked != null && invoices != null
      ? Math.max(0, checked - invoices)
      : null;
  const errors = run.error_count ?? 0;
  const open = run.open_error_count;
  const resolved = errors - open;
  return (
    <div className="tw:grid tw:grid-cols-3 tw:gap-4">
      <div className="tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-4">
        <p className="tw:text-xs tw:text-muted-foreground">Invoices created</p>
        <p className="tw:text-2xl tw:font-semibold tw:tabular-nums">
          {invoices != null ? formatCount(invoices) : '—'}
        </p>
        {invoices == null && (
          <p className="tw:text-xs tw:text-muted-foreground">
            Not recorded for this run — count the SUCCESS lines of the
            createdInvoices file
          </p>
        )}
      </div>
      <div className="tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-4">
        <p className="tw:text-xs tw:text-muted-foreground">Stores checked</p>
        <p className="tw:text-2xl tw:font-semibold tw:tabular-nums">
          {checked != null ? formatCount(checked) : '—'}
        </p>
        {nothingToBill != null && (
          <p className="tw:text-xs tw:text-muted-foreground">
            {formatCount(nothingToBill)} had nothing to bill
          </p>
        )}
      </div>
      <div
        className={cn(
          'tw:rounded-lg tw:border tw:bg-card tw:p-4',
          errors > 0 && open > 0
            ? 'tw:border-destructive/50'
            : 'tw:border-border',
        )}
      >
        <p
          className={cn(
            'tw:text-xs',
            errors > 0 && open > 0
              ? 'tw:font-medium tw:text-destructive'
              : 'tw:text-muted-foreground',
          )}
        >
          Errors
        </p>
        <p
          className={cn(
            'tw:text-2xl tw:font-semibold tw:tabular-nums',
            errors > 0 && open > 0 && 'tw:text-destructive',
          )}
        >
          {formatCount(errors)}
        </p>
        {errors > 0 && (
          <p
            className={cn(
              'tw:text-xs',
              open === 0
                ? 'tw:text-emerald-600 tw:dark:text-emerald-400'
                : 'tw:text-muted-foreground',
            )}
          >
            {open === 0
              ? 'All resolved manually'
              : `${formatCount(resolved)} of ${formatCount(errors)} resolved`}
          </p>
        )}
      </div>
    </div>
  );
}

function ChargeRunResultCard({ run }: { run: RunDetail }) {
  const completed = run.invoice_count ?? run.parent_run?.invoice_count;
  return (
    <div className="tw:flex tw:flex-col tw:gap-2 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-4">
      <p className="tw:text-sm tw:font-semibold">What this run did</p>
      {[
        // Honest phrasing: the exact completion count lives in the result
        // files, not on the run row — PO-gated drafts are routed to
        // purchasing-order requests instead of being completed, so not every
        // invoice of the generation run is completed here.
        `Invoices of the ${completed != null ? formatCount(completed) + '-invoice ' : ''}generation run completed in Billomat — PO-gated drafts routed to purchasing-order requests`,
        'SEPA payment methods created where missing — see the SEPA csv',
        'Invoices emailed to partners — results in invoices-send-results.csv',
      ].map((line) => (
        <p key={line} className="tw:flex tw:items-center tw:gap-2 tw:text-sm">
          <Check className="tw:size-4 tw:text-emerald-600 tw:dark:text-emerald-400" />
          {line}
        </p>
      ))}
    </div>
  );
}

export default function RunDetailPage(props: {
  match: { params: { id: string } };
}) {
  const runId = props.match.params.id;
  const history = useHistory();
  const capability = useInvoicingCapability();
  const [chargeRun, setChargeRun] = useState<ChargeModalRun | null>(null);
  const [retrying, setRetrying] = useState(false);

  const { data, loading, refetch, startPolling, stopPolling } =
    useInvoiceRunDetailQuery({
      variables: { id: runId },
      fetchPolicy: 'cache-and-network',
    });
  const run = data?.invoiceRun;

  // Poll while the run (or its charge run, or a draft regeneration) is still
  // moving.
  const shouldPoll =
    run != null &&
    (isRunActive(run) ||
      (run.charge_run != null &&
        run.charge_run.status !== InvoiceRunStatus.Done &&
        run.charge_run.status !== InvoiceRunStatus.Failed) ||
      run.collection_invoices.some((collection) =>
        collection.regenerations.some(isRegenerationOpen),
      ));
  useEffect(() => {
    if (!shouldPoll) return;
    startPolling(30_000);
    return () => stopPolling();
  }, [shouldPoll, startPolling, stopPolling]);

  useBreadcrumbs({
    id: 'monthly-invoicing-run',
    items: [
      { label: 'Finance' },
      { label: 'Monthly invoicing', href: Routes.monthlyInvoicing() },
      { label: run ? `Run ${shortRunId(run.id)}` : 'Run' },
    ],
  });

  const [retryMutation] = useRetryInvoiceRunMutation();
  const { download } = useResultFileDownload();

  const errorsCsvFile = useMemo(
    () =>
      run?.result_files.find((f) => f.name.toLowerCase().includes('error')) ??
      null,
    [run],
  );

  if (loading && !run) {
    return (
      <div className="tw:flex tw:justify-center tw:py-16">
        <Spinner className="tw:size-6" />
      </div>
    );
  }

  if (!run) {
    return (
      <div className="tw:flex tw:flex-col tw:items-start tw:gap-3 tw:py-8">
        <p className="tw:text-sm tw:text-muted-foreground">
          This run does not exist (or you cannot see it).
        </p>
        <Button
          variant="outline"
          onClick={() => history.push(Routes.monthlyInvoicing())}
        >
          <ArrowLeft className="tw:size-4" />
          All runs
        </Button>
      </div>
    );
  }

  const state = runPresentationState(run);
  const esStyle = isEsStylePeriod(run);
  const period = periodLabel(esStyle, run.year, run.iso_month);
  const charged = isCharged(run);
  const isChargeType = run.type === InvoiceRunType.CompleteAndCharge;

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await retryMutation({ variables: { id: run.id } });
      toast.success(`Run ${shortRunId(run.id)} queued for retry`);
      await refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="tw:mx-auto tw:flex tw:max-w-[1320px] tw:flex-col tw:gap-4 tw:pb-10">
      <div>
        <Button
          variant="ghost"
          size="sm"
          className="tw:-ml-2 tw:text-muted-foreground"
          onClick={() => history.push(Routes.monthlyInvoicing())}
        >
          <ArrowLeft className="tw:size-4" />
          All runs
        </Button>
      </div>

      <div className="tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-4">
        <div className="tw:flex tw:flex-col tw:gap-1.5">
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            <h1 className="tw:text-2xl tw:font-semibold">{period}</h1>
            <RunTypePill type={run.type} />
            <RunStatusLabel state={state} />
            {run.dry_run && <DryRunPill long />}
            {charged && <ChargedPill />}
          </div>
          <p className="tw:font-mono tw:text-xs tw:text-muted-foreground">
            {shortRunId(run.id)} ·{' '}
            {periodRangeLabel(esStyle, run.year, run.iso_month)}
          </p>
        </div>
        <div className="tw:flex tw:items-center tw:gap-2">
          <CapabilityChip capability={capability} />
          {state === 'failed' && (
            <GatedButton
              capability={capability}
              variant="outline"
              disabled={retrying}
              onClick={handleRetry}
            >
              <RotateCcw className="tw:size-4" />
              Retry run
            </GatedButton>
          )}
          {isReadyToCharge(run) && (
            <GatedButton
              capability={capability}
              onClick={() => setChargeRun(run)}
            >
              Complete &amp; charge
            </GatedButton>
          )}
        </div>
      </div>

      {state === 'stalled' && <StalledBanner run={run} />}
      {state === 'failed' && <FailedBanner run={run} />}

      <div className="tw:grid tw:grid-cols-[minmax(0,1fr)_284px] tw:items-start tw:gap-4">
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-4">
          {(state === 'running' || state === 'stalled') && (
            <InProgressCard run={run} />
          )}
          {state === 'done' && isGenerationRun(run) && <StatTiles run={run} />}
          {isChargeType && state === 'done' && (
            <ChargeRunResultCard run={run} />
          )}
          {state === 'done' && isGenerationRun(run) && !run.dry_run && (
            <CollectionInvoicesCard
              run={run}
              capability={capability}
              onChanged={() => void refetch()}
            />
          )}
          <ErrorsCard
            run={run}
            onChanged={() => void refetch()}
            onDownloadErrorsCsv={
              errorsCsvFile ? () => void download(errorsCsvFile) : null
            }
          />
        </div>
        <RightRail run={run} />
      </div>

      <ChargeModal
        run={chargeRun}
        onOpenChange={(open) => {
          if (!open) setChargeRun(null);
        }}
        onCharged={() => void refetch()}
      />
    </div>
  );
}
